import React, { useState } from 'react'
import {
    Menu,
    MenuList,
    MenuItem,
    MenuProps,
    MenuButton,
} from '@chakra-ui/react'
import { Button } from '@chakra-ui/react'
import { Tooltip } from '@chakra-ui/react'
import { Checkbox } from '@chakra-ui/react'
import type { ButtonProps } from '@chakra-ui/react'
import { Box, Text, HStack } from '@chakra-ui/react'
import { ChevronDownIcon, Search2Icon } from '@chakra-ui/icons'
import { Input, InputGroup, InputRightElement } from '@chakra-ui/react'
import SwitchButton from '~components/shared/switch-button'
import SharedStack from '~components/shared/wrappers/shared-stack'

interface BoolFilter {
    label: string
    value: boolean
    setValue: (value: boolean) => void
}

export interface FilterSelectOption {
    value: string
    label: string
    sublabel?: string
}

interface Props extends ButtonProps {
    label: string
    options: FilterSelectOption[]
    values: string[]
    setValues: (values: string[]) => void
    itemFlair?: (value: string) => React.ReactNode
    disabledReason?: string
    countOnlyDisplay?: boolean
    menuOverrides?: Omit<MenuProps, 'children'>
    boolFilters?: BoolFilter[]
}

const FilterSelect: React.FC<Props> = ({
    label,
    options,
    values,
    setValues,
    itemFlair,
    disabledReason,
    countOnlyDisplay,
    menuOverrides,
    boolFilters,
    ...props
}) => {
    const [search, setSearch] = useState<string>('')

    if (disabledReason) {
        return (
            <Tooltip label={disabledReason}>
                <Button
                    size="sm"
                    rounded="md"
                    variant={values.length > 0 ? 'solid' : 'outline'}
                    colorScheme={values.length > 0 ? 'brand' : 'gray'}
                    rightIcon={
                        values.length === 0 ? <ChevronDownIcon /> : undefined
                    }
                    isDisabled={true}
                    {...props}
                >
                    <HStack>
                        {values.length === 0 || countOnlyDisplay ? (
                            <>
                                <Text>{label}</Text>
                                {countOnlyDisplay && values.length > 0 && (
                                    <Box
                                        bg="blackAlpha.300"
                                        px={2}
                                        py={0.5}
                                        rounded="full"
                                    >
                                        <Text fontSize="xs">
                                            {values.length}
                                        </Text>
                                    </Box>
                                )}
                            </>
                        ) : (
                            <>
                                <Text>
                                    {
                                        options.find(o => o.value === values[0])
                                            ?.label
                                    }
                                </Text>
                                {values.length > 1 && (
                                    <Box
                                        bg="blackAlpha.300"
                                        px={2}
                                        py={0.5}
                                        rounded="full"
                                    >
                                        <Text fontSize="xs">
                                            +{values.length - 1}
                                        </Text>
                                    </Box>
                                )}
                            </>
                        )}
                    </HStack>
                </Button>
            </Tooltip>
        )
    }

    return (
        <Menu closeOnSelect={false} {...menuOverrides}>
            <MenuButton
                as={Button}
                size="sm"
                rounded="md"
                variant={values.length > 0 ? 'solid' : 'outline'}
                colorScheme={values.length > 0 ? 'brand' : 'gray'}
                rightIcon={
                    values.length === 0 ? <ChevronDownIcon /> : undefined
                }
                {...props}
            >
                <HStack>
                    {values.length === 0 || countOnlyDisplay ? (
                        <>
                            <Text>{label}</Text>
                            {countOnlyDisplay && values.length > 0 && (
                                <Box
                                    bg="blackAlpha.300"
                                    px={2}
                                    py={0.5}
                                    rounded="full"
                                >
                                    <Text fontSize="xs">{values.length}</Text>
                                </Box>
                            )}
                        </>
                    ) : (
                        <>
                            <Text>
                                {
                                    options.find(o => o.value === values[0])
                                        ?.label
                                }
                            </Text>
                            {values.length > 1 && (
                                <Box
                                    bg="blackAlpha.300"
                                    px={2}
                                    py={0.5}
                                    rounded="full"
                                >
                                    <Text fontSize="xs">
                                        +{values.length - 1}
                                    </Text>
                                </Box>
                            )}
                        </>
                    )}
                </HStack>
            </MenuButton>
            <MenuList as="ul" zIndex={99999}>
                <Box px={3} mb={2}>
                    <InputGroup size="sm" variant="flushed">
                        <InputRightElement>
                            <Search2Icon color="gray.500" />
                        </InputRightElement>
                        <Input
                            placeholder="Search"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                        />
                    </InputGroup>
                </Box>
                {!!boolFilters?.length && (
                    <SharedStack row mx={3} mb={3}>
                        {boolFilters?.map(({ label, value, setValue }) => (
                            <SwitchButton
                                key={label}
                                size="xs"
                                colorScheme="brand"
                                isChecked={value}
                                onClick={() => setValue(!value)}
                            >
                                {label}
                            </SwitchButton>
                        ))}
                    </SharedStack>
                )}
                <Box maxH="482px" overflowY="scroll">
                    {options
                        .filter(o =>
                            search
                                ? o.label
                                    .toLowerCase()
                                    .includes(search.toLowerCase()) ||
                                (o.sublabel &&
                                    o.sublabel
                                        .toLowerCase()
                                        .includes(search.toLowerCase()))
                                : true,
                        )
                        .map(({ value, label, sublabel }) => (
                            <MenuItem
                                key={value}
                                as="li"
                                cursor="pointer"
                                onClick={() =>
                                    values.includes(value)
                                        ? setValues([
                                            ...values.filter(
                                                v => v !== value,
                                            ),
                                        ])
                                        : setValues([...values, value])
                                }
                                _hover={{ bg: 'gray.50' }}
                                _active={{ bg: 'gray.100' }}
                                _focus={{ bg: 'gray.50' }}
                            >
                                <HStack>
                                    <Checkbox
                                        isChecked={values.includes(value)}
                                        colorScheme="brand"
                                        pointerEvents="none"
                                    />
                                    {itemFlair && itemFlair(value)}
                                    <Box>
                                        <Text fontSize="sm">{label}</Text>
                                        {sublabel && (
                                            <Text
                                                fontSize="2xs"
                                                color="gray.500"
                                                lineHeight="1"
                                                mt={0.5}
                                            >
                                                {sublabel}
                                            </Text>
                                        )}
                                    </Box>
                                </HStack>
                            </MenuItem>
                        ))}
                </Box>
                <Box px={3}>
                    <Button
                        variant="link"
                        size="xs"
                        onClick={() => setValues([])}
                    >
                        Clear selection
                    </Button>
                </Box>
            </MenuList>
        </Menu>
    )
}

export default FilterSelect
